import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useCompanyLogo } from "@/hooks/use-company-logo";

export const ROLE_LABELS: Record<string, string> = { tragare: "Trågare", bagare: "Bagare", pakare: "Pakare" };

export default function ProductionLogin({ role, onSuccess }: { role: string; onSuccess: () => void }) {
  const logoUrl = useCompanyLogo();
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/production-auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role, pin }),
        credentials: "include",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) setError(body.message || "Inloggningen misslyckades");
      else onSuccess();
    } catch {
      setError("Kontrollera din anslutning och försök igen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src={logoUrl} alt="Rätt Order" className="h-12" />
          </div>
          <CardTitle className="text-2xl">{ROLE_LABELS[role] || "Produktion"}</CardTitle>
          <CardDescription>Ange PIN-kod för att fortsätta</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pin">PIN-kod</Label>
              <Input id="pin" type="password" inputMode="numeric" autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value)} data-testid="input-production-pin" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="text-production-login-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={busy || !pin} data-testid="button-production-login">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Logga in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
